import type { JSX } from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Badge, Button, Select, type BadgeTone } from '@artosera/ui';
import { usePeut } from '../auth/usePeut.js';
import { ApiError, changerEtatDemandeSite, listerDemandesSite } from '../lib/api.js';
import type { DemandeSiteConsole, EtatDemandeSite, FiltreDemandesSite } from '../types.js';
import pageStyles from '../styles/page.module.css';
import tableStyles from '../styles/table.module.css';
import styles from './DemandesSite.module.css';

const CLE = ['console', 'demandes-site'] as const;
const FILTRES: readonly FiltreDemandesSite[] = [
  'ouvertes',
  'toutes',
  'envoyee',
  'en_cours',
  'traitee',
  'abandonnee',
];
const TONE: Record<EtatDemandeSite, BadgeTone> = {
  envoyee: 'reserved',
  en_cours: 'accent',
  traitee: 'available',
  abandonnee: 'withdrawn',
};

type Geste = Exclude<EtatDemandeSite, 'envoyee'>;

/** Les gestes permis depuis chaque état : ceux de l'API (`envoyee → …`, `en_cours → …`). */
const GESTES: Record<EtatDemandeSite, readonly { vers: Geste; cle: string }[]> = {
  envoyee: [
    { vers: 'en_cours', cle: 'prendre' },
    { vers: 'traitee', cle: 'traiter' },
    { vers: 'abandonnee', cle: 'abandonner' },
  ],
  en_cours: [
    { vers: 'traitee', cle: 'traiter' },
    { vers: 'abandonnee', cle: 'abandonner' },
  ],
  traitee: [],
  abandonnee: [],
};

/**
 * `/demandes-site` : les demandes de création de site envoyées par les
 * galeries. L'état est celui de l'API ; seuls l'exploitation et la direction
 * (`console:manage-onboarding`) le font avancer.
 */
export function DemandesSite(): JSX.Element {
  const { t, i18n } = useTranslation('console');
  const peut = usePeut();
  const peutAgir = peut('console:manage-onboarding');
  const queryClient = useQueryClient();
  const [filtre, setFiltre] = useState<FiltreDemandesSite>('ouvertes');
  const [erreur, setErreur] = useState<string | null>(null);

  const requete = useQuery({
    queryKey: [...CLE, filtre],
    queryFn: () => listerDemandesSite(filtre),
  });

  const changement = useMutation({
    mutationFn: ({ id, vers }: { id: string; vers: Geste }) => changerEtatDemandeSite(id, vers),
    onMutate: () => setErreur(null),
    onError: (cause) => {
      const raison = (cause instanceof ApiError ? cause.details : null) as {
        raison?: string;
      } | null;
      setErreur(
        raison?.raison === 'transition_interdite'
          ? t('demandesSite.erreurs.transition_interdite')
          : t('demandesSite.erreurs.generique'),
      );
    },
    // Dans tous les cas on relit : l'écran ne garde pas un état que le serveur a démenti.
    onSettled: () => queryClient.invalidateQueries({ queryKey: CLE }),
  });

  const date = (iso: string): string =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(new Date(iso));

  const demandes = requete.data ?? [];

  return (
    <section>
      <div className={pageStyles.entete}>
        <div>
          <h1>{t('demandesSite.titre')}</h1>
          <p>{t('demandesSite.texte')}</p>
        </div>
      </div>

      <div className={tableStyles.toolbar}>
        <div className={tableStyles.champFiltre}>
          <label htmlFor="demandes-site-filtre">{t('demandesSite.filtres.etat')}</label>
          <Select
            id="demandes-site-filtre"
            value={filtre}
            onValueChange={(valeur) => setFiltre(valeur as FiltreDemandesSite)}
            options={FILTRES.map((valeur) => ({
              value: valeur,
              label:
                valeur === 'ouvertes' || valeur === 'toutes'
                  ? t(`demandesSite.filtres.${valeur}`)
                  : t(`demandesSite.etat.${valeur}`),
            }))}
          />
        </div>
      </div>

      {erreur ? (
        <p role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
          {erreur}
        </p>
      ) : null}

      {requete.isLoading ? (
        <p className={pageStyles.videEtat}>{t('demandesSite.chargement')}</p>
      ) : requete.isError ? (
        <p role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
          {t('demandesSite.erreurChargement')}
        </p>
      ) : demandes.length === 0 ? (
        <p className={pageStyles.videEtat}>
          {filtre === 'ouvertes' ? t('demandesSite.videOuvertes') : t('demandesSite.vide')}
        </p>
      ) : (
        <ul className={styles.liste}>
          {demandes.map((demande) => (
            <li key={demande.id}>
              <CarteDemande
                demande={demande}
                date={date}
                peutAgir={peutAgir}
                enCours={changement.isPending}
                onGeste={(vers) => changement.mutate({ id: demande.id, vers })}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CarteDemande({
  demande,
  date,
  peutAgir,
  enCours,
  onGeste,
}: {
  demande: DemandeSiteConsole;
  date: (iso: string) => string;
  peutAgir: boolean;
  enCours: boolean;
  onGeste: (vers: Geste) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const [ouvert, setOuvert] = useState(false);
  const idDetail = useId();
  const nom = demande.galerie.nom ?? t('demandesSite.galerieSansNom');
  const gestes = peutAgir ? GESTES[demande.etat] : [];

  const resume: readonly (readonly [string, string])[] = [
    [t('demandesSite.resume.demandeur'), demande.demandeurEmail ?? demande.contact.email],
    [t('demandesSite.resume.moyen'), t(`demandesSite.moyen.${demande.contact.moyen}`)],
    [
      t('demandesSite.resume.langues'),
      demande.langues.map((langue) => t(`demandesSite.langue.${langue}`)).join(', '),
    ],
    [
      t('demandesSite.resume.visite'),
      t(demande.visiteVirtuelle ? 'demandesSite.oui' : 'demandesSite.non'),
    ],
    [t('demandesSite.resume.delai'), t(`demandesSite.delai.${demande.delai}`)],
    [t('demandesSite.resume.budget'), demande.budget ?? t('demandesSite.sansBudget')],
  ];

  return (
    <article className={styles.carte} aria-label={nom}>
      <header className={styles.entete}>
        <div className={styles.identite}>
          <h2 className={styles.nom}>
            <Link to={`/galeries/${demande.galerie.id}`}>{nom}</Link>
          </h2>
          <p className={styles.date}>
            {t('demandesSite.envoyeeLe', { date: date(demande.envoyeeLe) })}
          </p>
        </div>
        <Badge tone={TONE[demande.etat]}>{t(`demandesSite.etat.${demande.etat}`)}</Badge>
      </header>

      <dl className={styles.resume}>
        {resume.map(([libelle, valeur]) => (
          <div key={libelle}>
            <dt>{libelle}</dt>
            <dd>{valeur}</dd>
          </div>
        ))}
      </dl>

      <div className={styles.pied}>
        <Button
          variant="ghost"
          size="sm"
          aria-expanded={ouvert}
          aria-controls={idDetail}
          onClick={() => setOuvert((courant) => !courant)}
        >
          {t(ouvert ? 'demandesSite.detail.masquer' : 'demandesSite.detail.voir')}
        </Button>
        {gestes.length > 0 ? (
          <div className={styles.gestes}>
            {gestes.map(({ vers, cle }) => (
              <Button
                key={vers}
                variant={
                  vers === 'abandonnee' ? 'ghost' : vers === 'en_cours' ? 'primary' : 'secondary'
                }
                size="sm"
                disabled={enCours}
                onClick={() => onGeste(vers)}
              >
                {t(`demandesSite.actions.${cle}`)}
              </Button>
            ))}
          </div>
        ) : null}
      </div>

      {ouvert ? (
        <dl id={idDetail} className={styles.detail}>
          <div>
            <dt>{t('demandesSite.detail.objectifs')}</dt>
            <dd>{demande.objectifs}</dd>
          </div>
          {demande.message !== '' ? (
            <div>
              <dt>{t('demandesSite.detail.message')}</dt>
              <dd>{demande.message}</dd>
            </div>
          ) : null}
          <div>
            <dt>{t('demandesSite.detail.siteActuel')}</dt>
            <dd>
              {demande.siteExistant && demande.urlActuelle
                ? demande.urlActuelle
                : t('demandesSite.detail.aucunSite')}
            </dd>
          </div>
          <div>
            <dt>{t('demandesSite.detail.contact')}</dt>
            <dd>
              <span className={styles.bloc}>{demande.contact.nom}</span>
              <span className={styles.bloc}>{demande.contact.email}</span>
              {demande.contact.telephone ? (
                <span className={styles.bloc}>{demande.contact.telephone}</span>
              ) : null}
            </dd>
          </div>
        </dl>
      ) : null}
    </article>
  );
}
